import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { ErrorState } from '@/components/common/ErrorState';
import { LoadingState } from '@/components/common/LoadingState';
import { ROUTES } from '@/config/routes';
import { withRedirect } from '@/lib/authRedirect';
import { selectIsAuthenticated, selectIsWorkspaceMember, useAuthStore } from '@/stores/auth';

/**
 * 워크스페이스 세션 가드 — 진입 시 세션 복원 후 미인증 → FO 로그인(redirect 보존), 워크스페이스 사용자 유형 없음 → 접근 불가 안내.
 * 화면별 기능 허용은 라우트 `handle.feature` 를 읽는 `RequireRouteFeature` 가 본다. 클라 가드는 UX 레이어이며 실제 강제는 백엔드
 * (`WorkspaceRoles.MEMBER` + 기능 허용 인터셉터, specs/064).
 */
export function RequireWorkspaceMember() {
  const restored = useAuthStore((s) => s.sessionRestored);
  const restoreSession = useAuthStore((s) => s.restoreSession);
  const sessionError = useAuthStore((s) => s.sessionError);
  const retrySession = useAuthStore((s) => s.retrySession);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isMember = useAuthStore(selectIsWorkspaceMember);
  const location = useLocation();

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  if (sessionError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <ErrorState
          title="세션을 확인하지 못했습니다"
          message="일시적인 연결 문제일 수 있습니다. 잠시 후 다시 시도해 주세요."
          onRetry={() => void retrySession()}
        />
      </div>
    );
  }
  if (!restored) return <LoadingState fullscreen label="세션을 확인하는 중입니다" />;
  if (!isAuthenticated) {
    return (
      <Navigate to={withRedirect(ROUTES.login, location.pathname + location.search)} replace />
    );
  }
  if (!isMember) return <Navigate to={ROUTES.errorAuthRequired} replace />;
  return <Outlet />;
}
